import React, { lazy } from 'react';
import { SvgIconProps } from '@mui/material';
import { People, FileDownloadOutlined, ShowChart, FactCheckOutlined, ScienceOutlined, ChatBubbleOutline,
  PersonOutline, HourglassEmpty, BadgeOutlined, History, CalendarMonth, Tune, AssignmentOutlined, Warning } from '@mui/icons-material';
import type { StudyNavigationItem, StudyNavigationValue } from './StudyNavigation';
const FitbitCollectionDataView = lazy(() => import('../FitbitCollectionDataView'));
const StudyAdherenceMonitor = lazy(() => import('../adherence-monitor/StudyAdherenceMonitor'));
const DataCoverageDashboard = lazy(() => import('../data-coverage/data_coverage_dashboard'));
const StudyVisualizationDashboard = lazy(() => import('../visualization/StudyVisualizationDashboard'));
const PreferencesPanel = lazy(() => import('../PreferencesPanel'));
const ConsentManager = lazy(() => import('../../consent_management/components/researcher/ConsentManager'));
const StudyAssessments = lazy(() => import('../assessments/StudyAssessments'));
type PanelProps = { studyId: string; active?: boolean };
export type ParticipantView = 'participant' | 'pending';
export interface StudySection {
  tab: number;
  label: string;
  title?: string;
  group: StudyNavigationValue;
  Icon: React.ComponentType<SvgIconProps>;
  participantView?: ParticipantView;
  spotlightId?: string;
  Component?: React.ComponentType<PanelProps>;
}
const defineSections = <T extends Record<string, StudySection>>(sections: T) => sections;

// Add a section here to register its menu entry, URL tab, icon and embedded panel.
// Existing dashboard-owned forms keep their local state and use the same metadata.
export const studySections = defineSections({
  participants: { tab: 0, label: 'Active Participants', title: 'Participants', group: 'participants', Icon: PersonOutline, participantView: 'participant' },
  pending: { tab: 0, label: 'Pending Participants', title: 'Pending participants', group: 'participants', Icon: HourglassEmpty, participantView: 'pending' },
  tracking: { tab: 4, label: 'Tracking ID', title: 'Tracking IDs', group: 'participants', Icon: BadgeOutlined },
  email: { tab: 5, label: 'Email History', title: 'Email history', group: 'participants', Icon: History },
  extraction: { tab: 1, label: 'Data extraction', group: 'extraction', Icon: FileDownloadOutlined },
  assessments: { tab: 14, label: 'Assessments', group: 'assessments', Icon: AssignmentOutlined, Component: StudyAssessments },
  visualization: { tab: 7, label: 'Visualization', group: 'visualization', Icon: ShowChart,
    Component: ({ studyId }: PanelProps) => <FitbitCollectionDataView studyId={studyId} mode="visualization" embedded legacy={<StudyVisualizationDashboard embedded />} /> },
  coverage: { tab: 8, label: 'Data coverage', group: 'adherence', Icon: CalendarMonth,
    Component: ({ studyId }: PanelProps) => <FitbitCollectionDataView studyId={studyId} mode="coverage" embedded legacy={<DataCoverageDashboard embedded />} /> },
  adherence: { tab: 11, label: 'Study adherence', group: 'adherence', Icon: FactCheckOutlined,
    Component: ({ studyId }: PanelProps) => <StudyAdherenceMonitor studyId={studyId} embedded /> },
  alerts: { tab: 3, label: 'Alerts', group: 'adherence', Icon: Warning },
  members: { tab: 6, label: 'Study Members', title: 'Study members', group: 'study', Icon: People },
  preferences: { tab: 12, label: 'Preferences', group: 'study', Icon: Tune,
    Component: ({ studyId }: PanelProps) => <PreferencesPanel studyId={studyId} /> },
  consent: { tab: 13, label: 'Consent Management', group: 'study', Icon: AssignmentOutlined, spotlightId: 'consent-management-nav',
    Component: ({ studyId }: PanelProps) => <ConsentManager studyId={studyId} embedded /> },
  chat: { tab: 10, label: 'Chat', group: 'chat', Icon: ChatBubbleOutline },
});
export type StudySectionKey = keyof typeof studySections;
export const studySectionEntries = Object.entries(studySections) as [StudySectionKey, StudySection][];
export const validStudyTabs = new Set(studySectionEntries.map(([, section]) => section.tab));
export function sectionForTab(tab: number, participantView: ParticipantView = 'participant') {
  return studySectionEntries.find(([, section]) => section.tab === tab && (!section.participantView || section.participantView === participantView))?.[1];
}

const groups: { value: StudyNavigationValue; label?: string; Icon?: React.ComponentType<SvgIconProps>; menu?: boolean; spotlightId?: string }[] = [
  { value: 'participants', label: 'Participants', Icon: People, menu: true },
  { value: 'assessments' },
  { value: 'extraction' },
  { value: 'visualization' },
  { value: 'adherence', label: 'Adherence', Icon: FactCheckOutlined, menu: true },
  { value: 'study', label: 'Study', Icon: ScienceOutlined, menu: true, spotlightId: 'study-tab' },
  { value: 'chat' },
];

export function studyNavigationItems(tab: number, participantView: ParticipantView, select: (key: StudySectionKey) => void, chatLabel: React.ReactNode): StudyNavigationItem[] {
  return groups.map(({ Icon: GroupIcon, menu, ...group }) => {
    const entries = studySectionEntries.filter(([, section]) => section.group === group.value);
    const Icon = GroupIcon || entries[0][1].Icon;
    const base = { ...group, icon: <Icon />, label: group.value === 'chat' ? chatLabel : group.label || entries[0][1].label };
    return menu ? { ...base, options: entries.map(([key, section]) => ({
      label: section.label, icon: <section.Icon />, spotlightId: section.spotlightId,
      selected: section.tab === tab && (!section.participantView || section.participantView === participantView),
      onSelect: () => select(key),
    })) } : { ...base, onSelect: () => select(entries[0][0]) };
  });
}
